// ============================================================================
// File: frontend/src/components/dashboard/OfficeUpdatesWidget.js
// Version: 128.0
// Date: 2025-12-12 02:50:00 EST
// Author: Dashboard Team
// Description: Office Updates widget - displays recent office activity (leads, cases, tasks, deadlines).
//              Compact design with colorful Lucide icons. Shows 5+ items with scrollbar.
//              Separated from AI Insights to match Photoshop design.
// 
// Changelog:// v128.0 (2025-12-12) - 🎯 FIXED HEIGHT
//                     - Set maxHeight: 450px to fit on screen
//                     - Both widgets visible simultaneously
//                     - Matches Photoshop proportions
// v127.0 (2025-12-12) - Separated component with colorful iconsns
//                     - Scrollbar for 25 items
// 
// DEPLOYMENT:
// Copy this file to: frontend/src/components/dashboard/OfficeUpdatesWidget.js
// ============================================================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { 
    Activity, Zap, Clock,
    UserPlus, FileText, Briefcase, Calendar, 
    CheckCircle, AlertCircle, MessageCircle, FileCheck
} from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    canvas:      'var(--canvas, #f8fafc)',  // класс A
    ink:         'var(--ink, #1e293b)',  // класс A
    lineStrong:  'var(--line, #cbd5e1)',  // класс A
    muted:       'var(--ink-muted, #64748b)',  // класс A
    mutedSoft:   'var(--ink-muted, #94a3b8)',  // класс A
    surfaceSoft: 'var(--surface-soft, #f1f5f9)',  // класс A
    amber1:      'var(--amber-accent, #f59e0b)',  // класс B
    blue1:       'var(--blue-accent, #3b82f6)',  // класс B
    blue8:       'var(--blue-accent, #6366f1)',  // класс B
    purple2:     'var(--purple-accent, #8b5cf6)',  // класс B
    red5:        'var(--red-accent, #ec4899)',  // класс B
    teal1:       'var(--green-accent, #10b981)',  // класс B
    teal4:       'var(--green-accent, #06b6d4)',  // класс B
    chipAmber1:  'var(--chip-amber-bg, #fef3c7)',  // класс C
    chipBlue2:   'var(--chip-blue-bg, #dbeafe)',  // класс C
    chipBlue3:   'var(--chip-blue-bg, #e0e7ff)',  // класс C
    chipGreen4:  'var(--chip-green-bg, #d1fae5)',  // класс C
    chipPurple2: 'var(--chip-purple-bg, #ede9fe)',  // класс C
    chipRed3:    'var(--chip-red-bg, #fce7f3)',  // класс C
    chipTeal1:   'var(--chip-green-bg, #cffafe)',  // класс C
};




/*
 * Переход по элементу ленты. У элементов уже есть id вида `lead_<n>`
 * (FeedService.get_feeds) — берём номер оттуда.
 *
 * 🔴 Элемент, чей id не разбирается, остаётся НЕкликабельным. Вести «куда-то»
 * хуже, чем не вести никуда: человек решит, что перешёл, и будет читать чужую
 * или пустую страницу.
 */
const leadIdOf = (item) => {
    const m = /^lead_(\d+)$/.exec(String(item && item.id ? item.id : ''));
    return m ? m[1] : null;
};

const OfficeUpdatesWidget = ({ events }) => {
    const navigate = useNavigate();
    const items = events || [];
    const limitedItems = items.slice(0, 25);

    // Icon mapper with colors
    const getIcon = (item) => {
        const title = (item.title || '').toLowerCase();
        const type = (item.type || '').toLowerCase();
        
        if (title.includes('lead') || title.includes('prospect')) {
            return { Icon: UserPlus, color: P.blue1, bg: P.chipBlue2 };
        }
        if (title.includes('case') || title.includes('contract')) {
            return { Icon: Briefcase, color: P.purple2, bg: P.chipPurple2 };
        }
        if (title.includes('completed') || title.includes('finished') || type === 'success') {
            return { Icon: CheckCircle, color: P.teal1, bg: P.chipGreen4 };
        }
        if (title.includes('deadline') || title.includes('due') || type === 'warning') {
            return { Icon: AlertCircle, color: P.amber1, bg: P.chipAmber1 };
        }
        if (title.includes('document') || title.includes('file')) {
            return { Icon: FileCheck, color: P.teal4, bg: P.chipTeal1 };
        }
        if (title.includes('meeting') || title.includes('appointment')) {
            return { Icon: Calendar, color: P.red5, bg: P.chipRed3 };
        }
        if (title.includes('message') || title.includes('chat')) {
            return { Icon: MessageCircle, color: P.blue8, bg: P.chipBlue3 };
        }
        return { Icon: FileText, color: P.muted, bg: P.surfaceSoft };
    };

    if (limitedItems.length === 0) {
        return null;
    }

    return (
        <div className="card" style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            background: 'white', 
            borderRadius: '16px', 
            overflow: 'hidden',
            border: `1px solid ${P.surfaceSoft}`, 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            minHeight: '475px',
            maxHeight: '900px'
        }}>
            {/* HEADER */}
            <div style={{ 
                padding: '20px 24px 16px 24px', 
                borderBottom: `1px solid ${P.canvas}`, 
                flexShrink: 0,
                background: 'white'
            }}>
                <h3 style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '700', 
                    color: P.ink, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    margin: 0,
                    marginBottom: '8px'
                }}>
                    <Activity size={20} style={{ stroke: P.blue1 }} /> Recent Activity
                </h3>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                }}>
                    <Zap size={14} style={{ stroke: P.amber1, fill: P.amber1 }} />
                    <span style={{ 
                        fontSize: '0.7rem', 
                        fontWeight: '800', 
                        color: P.mutedSoft, 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.5px'
                    }}>
                        OFFICE UPDATES
                    </span>
                </div>
            </div>
            
            {/* SCROLLABLE CONTENT */}
            <div style={{ 
                flex: 1,
                overflowY: 'auto',
                padding: '16px 24px',
                minHeight: 0
            }} className="office-scroll">
                {limitedItems.map((item, idx) => {
                    const { Icon, color, bg } = getIcon(item);

                    const leadId = leadIdOf(item);

                    return (
                        <div
                            key={item.id || idx}
                            onClick={leadId ? () => navigate(`/leads/${leadId}`) : undefined}
                            role={leadId ? 'link' : undefined}
                            tabIndex={leadId ? 0 : undefined}
                            onKeyDown={leadId ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') navigate(`/leads/${leadId}`);
                            } : undefined}
                            title={leadId ? 'Открыть историю обращения' : undefined}
                            style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            marginBottom: '14px',
                            paddingBottom: '14px',
                            cursor: leadId ? 'pointer' : 'default',
                            borderBottom: idx < limitedItems.length - 1 ? `1px solid ${P.surfaceSoft}` : 'none'
                        }}>
                            {/* Icon circle */}
                            <div style={{ 
                                width: '32px',
                                height: '32px', 
                                borderRadius: '50%', 
                                background: bg, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                border: `2px solid ${color}`,
                                flexShrink: 0,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                            }}>
                                <Icon size={16} color={color} />
                            </div>
                            
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                    marginBottom: '4px'
                                }}>
                                    <div style={{ 
                                        fontWeight: '600', 
                                        fontSize: '0.9rem',
                                        color: P.ink,
                                        lineHeight: '1.3',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1
                                    }}>
                                        {item.title}
                                    </div>
                                    <span style={{ 
                                        fontSize: '0.7rem',
                                        color: P.mutedSoft, 
                                        whiteSpace: 'nowrap',
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '4px',
                                        flexShrink: 0
                                    }}>
                                        <Clock size={11} />
                                        {item.time}
                                    </span>
                                </div>
                                <div style={{ 
                                    fontSize: '0.8rem',
                                    color: P.muted, 
                                    lineHeight: '1.4',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {item.description}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* Custom scrollbar styles */}
            <style>{`
                .office-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .office-scroll::-webkit-scrollbar-track {
                    background: ${P.canvas};
                    border-radius: 10px;
                }
                .office-scroll::-webkit-scrollbar-thumb {
                    background: ${P.lineStrong};
                    border-radius: 10px;
                }
                .office-scroll::-webkit-scrollbar-thumb:hover {
                    background: ${P.mutedSoft};
                }
                .office-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: ${P.lineStrong} ${P.canvas};
                }
            `}</style>
        </div>
    );
};

OfficeUpdatesWidget.propTypes = {
    events: PropTypes.array
};

OfficeUpdatesWidget.defaultProps = {
    events: []
};

export default OfficeUpdatesWidget;